import { type ReactNode, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { describeError } from '@/lib/auth';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Label } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

/**
 * The shape every owner action takes: say why, prove it is you, then act.
 *
 * The authenticator code is the part that matters. A hospital's records sit
 * behind these buttons, and an owner console left unlocked on a desk — or a
 * session token lifted off one — must not be enough to reach them. The reason is
 * for the other half of the problem: six months later somebody asks who reset
 * the consultant's password, and the audit trail should answer without anyone
 * having to remember.
 */

export function TotpField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5 rounded-md border border-primary/30 bg-primary/5 p-3">
      <Label htmlFor="owner-totp" className="flex items-center gap-1.5 text-xs">
        <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Enter your 2FA code to confirm
      </Label>
      <Input
        id="owner-totp"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        className="tabular tracking-[0.4em]"
        placeholder="000000"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      />
    </div>
  );
}

export const isValidTotp = (value: string): boolean => /^\d{6}$/.test(value);

export interface OwnerConfirmProps<T> {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Shown above the reason box — what this action will actually do. */
  warning?: ReactNode;
  /** Anything extra the action needs, e.g. a typed-slug confirmation. */
  children?: ReactNode;
  /** True when `children`'s own requirements are met. */
  extraValid?: boolean;
  confirmLabel: string;
  destructive?: boolean;
  minReason?: number;
  wide?: boolean;
  action: (payload: { totpCode: string; reason: string }) => Promise<T>;
  onDone: (result: T) => void;
  successMessage?: string | ((result: T) => string | undefined);
}

export function OwnerConfirmDialog<T>({
  open,
  onClose,
  title,
  description,
  warning,
  children,
  extraValid = true,
  confirmLabel,
  destructive,
  minReason = 5,
  wide,
  action,
  onDone,
  successMessage,
}: OwnerConfirmProps<T>) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const run = useMutation({
    mutationFn: () => action({ totpCode, reason: reason.trim() }),
    onSuccess: (result) => {
      const message = typeof successMessage === 'function' ? successMessage(result) : successMessage;
      if (message) toast.success(message);
      setReason('');
      setTotpCode('');
      onDone(result);
    },
    onError: (error) => toast.error(`Could not ${confirmLabel.toLowerCase()}`, describeError(error)),
  });

  const ready = reason.trim().length >= minReason && isValidTotp(totpCode) && extraValid;

  const close = () => {
    setReason('');
    setTotpCode('');
    onClose();
  };

  return open ? (
    <ConfirmDialog
      onClose={close}
      title={title}
      description={description}
      wide={wide}
      destructive={destructive}
      confirmLabel={confirmLabel}
      loading={run.isPending}
      disabled={!ready}
      onConfirm={() => run.mutate()}
    >
      <div className="space-y-4">
        {warning && <Alert variant={destructive ? 'warning' : 'default'}>{warning}</Alert>}
        {children}
        <Field label="Reason" required hint="Recorded in the platform audit trail — a ticket number is ideal.">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <TotpField value={totpCode} onChange={setTotpCode} />
      </div>
    </ConfirmDialog>
  ) : null;
}

/**
 * A password the operator gets exactly one look at.
 *
 * Deliberately its own dialog rather than a toast: a toast that disappears after
 * four seconds while somebody is still writing on a sticky note is how support
 * calls get made twice.
 */
export function TempPasswordDialog({
  open,
  onClose,
  who,
  username,
  password,
}: {
  open: boolean;
  onClose: () => void;
  who: string;
  username: string;
  password: string;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="One-time password"
      description={`Read it to ${who} now — it is not stored and cannot be shown again.`}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <div className="space-y-3">
        <div className="rounded-md border bg-muted/40 p-4 text-center">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Username</div>
          <div className="font-mono text-lg tracking-wide">{username}</div>
          <div className="mt-3 text-xs uppercase tracking-wide text-muted-foreground">Password</div>
          <div className="font-mono text-lg tracking-wide">{password}</div>
        </div>
        <Alert>
          They must change it at their next sign-in, and every session they had open has already
          ended.
        </Alert>
      </div>
    </Dialog>
  );
}
